import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibRadio } from '@chewy/kib-choices-react'
import { KibField } from '@chewy/kib-fields-new-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import { type User, Utils } from '@pbt/pbt-ui-components'

import { fetchClient } from '~/store/actions/clients'
import { getUser } from '~/store/reducers/users'

import type {
  DeliveryMethod,
  SendToRecipient,
} from '../constants/paymentRequestDialogV2Constants'
import { usePaymentRequestDialog } from '../context/PaymentRequestDialogProvider'
import { formatSendToDisplay } from '../utils/formatSendToDisplay'

import styles from './DeliverySection.module.scss'

const mapClientToSendToRecipient = (client: User): SendToRecipient => ({
  id: client.id,
  name: Utils.getPersonString(client),
  email: client.email,
  phone: client.mobilePhone,
})

export const DeliverySection = () => {
  const { t } = useTranslation(['Common', 'Dialogs'])
  const dispatch = useDispatch()
  const { invoice, dialogState, actions } = usePaymentRequestDialog()
  const { deliveryMethod, sendTo } = dialogState
  const clientId = invoice?.clientId
  const client = useSelector(getUser(clientId))

  useEffect(() => {
    if (clientId && !client?.email) {
      dispatch(fetchClient({ clientId }))
    }
  }, [clientId, client?.email, dispatch])

  useEffect(() => {
    if (client?.id) {
      actions.setSendTo([mapClientToSendToRecipient(client)])
    }
  }, [client?.id, client?.email, client?.mobilePhone, actions])

  const sendToLabel =
    deliveryMethod === 'email'
      ? t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.SEND_EMAIL_TO')
      : t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.SEND_MESSAGE_TO')

  const sendToDisplayValue = formatSendToDisplay(sendTo, deliveryMethod)

  return (
    <div className={styles.deliverySectionContainer}>
      <div className={styles.deliverMethodSection}>
        <KibInputGroup
          className={styles.deliverMethodSectionLabel}
          label={t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.SEND_LINK_AS')}
        >
          <KibRadio
            label={t('Common:EMAIL')}
            name="payment-request-delivery-method"
            selectedValue={deliveryMethod}
            value={'email' as DeliveryMethod}
            onChange={(value) =>
              actions.setDeliveryMethod(value as DeliveryMethod)
            }
          />
          {/*
            TODO:CVC-36744 Disable the text message option when the client has opted out of receiving
            texts
          */}
          <KibRadio
            label={t('Common:TEXT_MESSAGE')}
            name="payment-request-delivery-method"
            selectedValue={deliveryMethod}
            value={'sms' as DeliveryMethod}
            onChange={(value) =>
              actions.setDeliveryMethod(value as DeliveryMethod)
            }
          />
        </KibInputGroup>
      </div>
      <KibField
        readOnly
        className={styles.sendToField}
        label={sendToLabel}
        value={sendToDisplayValue}
      />
    </div>
  )
}
